/**
 * Service landing page content.
 *
 * Lives under src/ because that is what Tailwind's content glob covers --
 * anything holding a class string outside it would silently fail to generate.
 * (The glob used to be a list of specific directories, which is exactly how
 * that silent failure happened once already; see tailwind.config.ts.)
 *
 * DELIBERATELY NO PRICING. These pages answer "what is this and what does it
 * involve". The money question is answered on the home page and in its FAQ, in
 * one place, so there is only ever one set of numbers to keep current. Please
 * do not reintroduce rates, minimums, contract terms or plan names here --
 * including in metaDescription, which is the easiest one to miss.
 *
 * Claims are otherwise grounded in what the site already states publicly:
 * Shopify Partner status, building on Shopify exclusively, the capability list
 * in the home page marquee, the scope limits already published, and the Lulu's
 * Sweets Boutique figures. No timelines, guarantees or results appear here
 * that are not already published elsewhere on the site.
 */

/**
 * Master switch for the service pages. Flip to true to bring them back.
 *
 * Everything below stays exactly as written -- this only decides whether it is
 * reachable. When false:
 *   - /services and /services/<slug> return 404 (see their page.tsx)
 *   - the footer drops its Services column and the "All services" link, and
 *     rebalances its grid to fill the space
 *   - the sitemap omits every services URL
 *
 * Annotated `: boolean` on purpose. Without it TypeScript infers the literal
 * type `false`, narrows every guard, and reports the enabled branches as dead
 * code -- which is exactly the code we are preserving.
 */
export const SERVICES_ENABLED: boolean = false;

export interface ServiceFaq {
  question: string;
  answer: string;
}

export interface Service {
  slug: string;
  /** Nav label and card title. */
  name: string;
  /** <title>, without the brand suffix -- the layout's title template
   *  appends "| Butter Websites". Kept short so the whole thing stays
   *  under ~60 characters and is not truncated in results. */
  metaTitle: string;
  /** Meta description, ~150-160 characters. */
  metaDescription: string;
  h1: string;
  lead: string;
  /** One-liner for the nav dropdown and the /services index. */
  summary: string;
  /** "What's included" grid. */
  includes: { title: string; body: string }[];
  /** Prose blocks, each with its own subheading. */
  sections: { heading: string; body: string }[];
  faqs: ServiceFaq[];
  /** Slugs cross-linked at the foot of the page. */
  related: string[];
}

export const services: Service[] = [
  {
    slug: 'shopify-web-development',
    name: 'Shopify Web Development',
    metaTitle: 'Shopify Web Development',
    metaDescription:
      'Shopify Partners designing and building custom stores for independent boutiques and artisan food brands, then staying on to run them.',
    h1: 'Shopify stores built to sell',
    lead: 'We design, build, and launch Shopify stores with clean and modern designs that are optimized for user experience and operational efficiency.',
    summary:
      'A custom Shopify store designed, built and launched, then looked after.',
    includes: [
      {
        title: 'User experience prioritized',
        body: "We start with the user experience and work backwards from there ensuring your website actually accomplishes it's primary business goals.",
      },
      {
        title: 'Modern best practices',
        body: "With our more than a decade of web experience and the tens of thousands of orders processed in client stores, we are able to effeciently deliver a reliable, secure, mobile-friendly, and fast website that today's customers now expect.",
      },
      {
        title: 'Product and collection structure',
        body: 'Variants, options, collections, tags, oh my! We thouroughly consider the best way to structure and organize your product catalog for long-term success and extensibility within the Shopify ecosystem like 3rd party apps and marketing integrations.',
      },
      {
        title: 'Essential features',
        body: 'Included in every build are essential features like reviews, customer accounts, store pickup, local delivery, national shipping, advanced product options, rewards and loyalty programs.',
      },
      {
        title: 'Business strategy',
        body: 'A fresh pair of eyes with deep knowledge of your business and product catalog to help you identify opportunities for growth and effeciency.',
      },
      {
        title: 'Technical SEO from day one',
        body: 'Titles, metadata, structured data, clean URLs and a sitemap in place at launch, so the store is legible to search engines from the moment it goes live.',
      },
    ],
    sections: [
      {
        heading: "Shopify's powerful platform",
        body: 'Shopify is without a doubt the most powerful merchant platform on the market. Getting your store and website setup is only the beginning when it comes to utilizing all of its features and capabilities that can help your business grow and run more efficiently.',
      },
      {
        heading: 'User experience matters',
        body: "Long gone are the days where a good enough website will do. Customers don't just appreciate a website that looks good, they expect it to be functional, fast, and easy to use.",
      },
      {
        heading: 'Store launch is just the beginning',
        body: 'Where most traditional web developers hand you the keys and say good luck, we treat your store launch as day 0. Your business never stops changing, and neither should your website. We are on call to help you with product and content updates, security and platform updates, ongoing SEO, and a second opinion whenever you want one.',
      },
    ],
    faqs: [
      {
        question: 'What do you need from me to start?',
        answer:
          'Your products, your brand assets, and a sense of how you want to sell: pickup, shipping, subscriptions, in person, or some combination. If parts of that are not settled yet, working them out with you is part of the job.',
      },
      {
        question: 'Do you build on platforms other than Shopify?',
        answer:
          "No. We are Shopify Partners and we build on Shopify exclusively. We genuinely believe that Shopify is the best of the best and we're all-in on it.",
      },
      {
        question: 'Do I own the store you build?',
        answer:
          'Yes. We initially build your store as a temporary "development store" and then once you love it and are ready to go live, we transfer everything over to your Shopify account and domain. From that point forward, you own 100% of it.',
      },
    ],
    related: ['shopify-store-maintenance', 'shopify-seo'],
  },

  {
    slug: 'shopify-point-of-sale',
    name: 'Shopify Point of Sale',
    metaTitle: 'Shopify POS Setup for Retail',
    metaDescription:
      'Shopify POS set up so your shop floor and your online store share one inventory, one customer list and one set of numbers.',
    h1: 'A unified backend for your business',
    lead: 'Experience all of your sales channels and third-party integrations synced and working together seamlessly with Shopify POS.',
    summary:
      'Shopify POS set up so your shop floor and your online store share one system.',
    includes: [
      {
        title: 'One source of truth',
        body: 'Sell the last one on the shop floor and it comes off the website. No more overselling something you no longer have, and no more evening spent reconciling two counts.',
      },
      {
        title: 'POS hardware',
        body: 'We can help setup registers, card readers, receipt printers, and staff permissions set so people can do their jobs without being able to do everything.',
      },
      {
        title: 'Local pickup and delivery',
        body: 'Buy online, pick up in store, and local delivery zones configured so the handoff between the website and the counter is something your staff can actually run.',
      },
      {
        title: 'One customer list',
        body: 'The person who buys in the shop and the person who buys online are the same customer record, which is what makes loyalty programs and email marketing worth doing.',
      },
      {
        title: 'Loyalty and referrals',
        body: 'Loyalty and referral programs that work the same at the register and at checkout, rather than a card in a wallet that the website knows nothing about.',
      },
      {
        title: 'Reporting that adds up',
        body: 'Production and sales reporting across both channels, so you are looking at one set of numbers when you decide what to make more of.',
      },
    ],
    sections: [
      {
        heading: 'Make the most of the Shopify ecosystem',
        body: "Shopify's real advantage is everything built around it: loyalty, reviews, subscriptions, reporting, pre-orders. The catch is that plenty of those apps were written for the online store and quietly do nothing at the register, and a few of them fight each other once both are installed. We have spent years finding out which combinations actually hold up in a working shop, so you end up with the ones that pull their weight on both sides of the counter instead of another subscription you cancel in three months.",
      },
      {
        heading: 'Pre-orders, simplified',
        body: 'Pre-orders are how a lot of boutiques and food businesses really sell: holiday boxes, custom cakes, seasonal drops, a run of something that has not been made yet. We set them up so an order placed online weeks ago can be found, fulfilled and handed over at the register in a few taps, instead of someone digging through a paper list or a shared inbox with a queue forming. Scheduled pickup dates, the order itself and the handoff all live in the same place your walk-in sales do.',
      },
      {
        heading: 'Grow your mailing list',
        body: "Shopify POS can ask for an email at checkout to send a digital receipt, and with the customer's consent that address joins the same list your online store is building. It is a small thing that compounds: a shop doing a few dozen transactions a day is quietly adding people who have already handed you money, which is the most valuable list you can own. We set the prompt up so it takes a couple of seconds and never slows the counter down.",
      },
    ],
    faqs: [
      {
        question: 'Is Shopify POS required?',
        answer:
          'No. While we strongly recommend using Shopify POS, it is not required. We can set up a Shopify store without POS and then add it later if you decide you need it.',
      },
      {
        question: 'Can you set up multiple store locations?',
        answer:
          'Yes. Multiple store locations are part of what we handle, including inventory and reporting split by location.',
      },
      {
        question: 'Will my in-store sales show up in the same reports?',
        answer:
          'Yes, that is most of the point. In-person and online sales land in the same reporting, against the same customer records and the same inventory.',
      },
    ],
    related: ['shopify-store-maintenance', 'shopify-web-development'],
  },

  {
    slug: 'shopify-store-maintenance',
    name: 'Shopify Store Maintenance',
    metaTitle: 'Shopify Store Maintenance',
    metaDescription:
      'Ongoing Shopify maintenance: content and product updates, security and platform updates, technical SEO and unlimited bug fixes.',
    h1: 'Designed to help you scale',
    lead: 'Most agencies build the store, hand you the keys and disappear. We do the opposite. We stay on to keep it running, updated and improving long after launch.',
    summary:
      'Ongoing updates, fixes and platform upkeep so the store does not quietly go stale.',
    includes: [
      {
        title: 'Product and content updates',
        body: 'New products, seasonal collections, homepage changes and copy edits handled for you. You send what changed; you do not have to learn the admin to make it happen.',
      },
      {
        title: 'Security and platform updates',
        body: 'Shopify, your theme and your apps kept current. Platform changes tend to break things quietly, which is exactly why nobody notices until a customer does.',
      },
      {
        title: 'Unlimited bug fixes',
        body: 'When something breaks, it gets fixed. Not scoped, not deferred, not added to a backlog for the next engagement.',
      },
      {
        title: 'Ongoing technical SEO',
        body: 'Search does not stand still. Metadata, structure and site health get revisited rather than set once at launch and left to drift.',
      },
      {
        title: 'Strategy on call',
        body: 'A second opinion whenever you want one, on a new product line, a promotion, an app someone is trying to sell you, or whether a change is worth making at all.',
      },
      {
        title: 'Automation for the repetitive work',
        body: 'Order routing, inventory alerts and customer follow-up built to run themselves, so the same half hour is not spent by hand every week.',
      },
    ],
    sections: [
      {
        heading: 'Why a store needs looking after at all',
        body: 'A Shopify store is not a brochure that sits still. The platform updates, apps change, payment rules change, your catalogue changes, and search changes what it rewards. Left alone for a year, a good store becomes a slightly broken one, usually in small ways that cost orders before anyone notices.',
      },
      {
        heading: 'On call, not on a ticket queue',
        body: 'We are a two person team, which means you are talking to the people doing the work. There is no account manager relaying your request to someone who has never seen your store.',
      },
      {
        heading: 'What we actually handle',
        body: 'Maintenance, content and product updates, security and platform updates, technical SEO, unlimited bug fixes, and strategy help when you want a second opinion. Content and product updates are limited to 10 a month.',
      },
    ],
    faqs: [
      {
        question: 'What if I already have a Shopify store?',
        answer:
          'We will take it over. Whether it is already on Shopify or on a platform you have outgrown, we start by looking at what you have and telling you what we would change about it.',
      },
      {
        question: 'Is there a limit on updates?',
        answer:
          'Content and product updates are limited to 10 a month. Bug fixes are unlimited. If something is broken, it gets fixed.',
      },
      {
        question: 'How do I ask for a change?',
        answer:
          'Email us and say what changed. A new product, a new photo, different copy, a collection that needs rearranging before the weekend. You should not have to learn the Shopify admin to run your own shop.',
      },
    ],
    related: ['shopify-web-development', 'shopify-point-of-sale'],
  },

  {
    slug: 'shopify-seo',
    name: 'Shopify SEO',
    metaTitle: 'Shopify SEO Services',
    metaDescription:
      'Technical SEO for Shopify stores, handled as ongoing work rather than a one-off audit: structure, metadata, speed and local search.',
    h1: 'Get found by people already looking for what you make',
    lead: 'Search is the cheapest customer you will ever get, and it compounds. We handle technical SEO as ongoing work, not as a one-off audit that lands in your inbox as a PDF and is out of date by spring.',
    summary:
      'Technical SEO, structure and local search, handled continuously rather than once.',
    includes: [
      {
        title: 'Technical foundations',
        body: 'Titles, metadata, heading structure, structured data, clean URLs, sitemaps and crawlability. The unglamorous layer everything else depends on.',
      },
      {
        title: 'Product and collection pages',
        body: 'The pages that actually rank for what you sell, structured so search engines understand what a product is and customers understand why they want it.',
      },
      {
        title: 'Site speed and Core Web Vitals',
        body: 'Image handling, script bloat and theme weight kept in check. Slow stores lose people before the page finishes drawing, and search notices.',
      },
      {
        title: 'Google Business Profile',
        body: 'For businesses with a shop floor, local search is often the highest-intent traffic there is. We set up and maintain your Google Business Profile alongside the site.',
      },
      {
        title: 'Analytics you can read',
        body: 'Google Analytics and search reporting configured so you can see what is actually bringing people in, rather than a dashboard nobody opens twice.',
      },
      {
        title: 'Reviews and social proof',
        body: 'UGC product reviews and social proof set up on the storefront. They influence both the customer reading them and how your pages are understood.',
      },
    ],
    sections: [
      {
        heading: 'What this looks like in practice',
        body: "Lulu's Sweets Boutique, an independent bakery we build and maintain for, has seen over 4 million Google search impressions and more than $500,000 in direct online sales since publishing their new site. Search was a large part of getting there.",
      },
      {
        heading: 'SEO is maintenance, not a project',
        body: 'An audit delivered once describes a store that no longer exists six months later. Search changes what it rewards, you add products, and the platform shifts underneath you. Treating SEO as continuous work is the only version of it that keeps paying.',
      },
      {
        heading: 'No promises about rankings',
        body: 'Anyone guaranteeing you a position is guessing, or worse. What we will do is make sure nothing about your store is standing in the way, and then keep it that way.',
      },
    ],
    faqs: [
      {
        question: 'What does technical SEO actually involve?',
        answer:
          'Titles and metadata, heading structure, structured data, clean URLs, sitemaps, crawlability, site speed and internal linking. It is the layer that decides whether a search engine can understand your store at all, before anything you have written has a chance to matter.',
      },
      {
        question: 'How long does SEO take to work?',
        answer:
          'Longer than anyone would like, and it depends on your products, your competition and where you are starting from. Anyone giving you a date is guessing.',
      },
      {
        question: 'Do you do local SEO for a physical shop?',
        answer:
          'Yes. Google Business Profile setup and upkeep is part of what we handle, which for a business with a shop floor is often the highest-intent search traffic available.',
      },
    ],
    related: ['shopify-email-marketing', 'shopify-store-maintenance'],
  },

  {
    slug: 'shopify-email-marketing',
    name: 'Shopify Email Marketing',
    metaTitle: 'Shopify Email Marketing',
    metaDescription:
      'Email and SMS marketing for Shopify, set up and run for you: automations, campaigns and a customer list you own outright.',
    h1: 'The one marketing channel you actually own',
    lead: 'Social platforms rent you an audience and change the terms whenever they like. Your email list is yours. We set up the automations, write the campaigns and send them.',
    summary:
      'Automations, campaigns and SMS built on a customer list that belongs to you.',
    includes: [
      {
        title: 'Automations that run themselves',
        body: 'Welcome sequences, abandoned checkout recovery and post-purchase follow-up: the messages that earn their keep without anyone writing them each week.',
      },
      {
        title: 'Campaigns for what is actually happening',
        body: 'New products, seasonal collections, promotions and events. Written and sent, not handed to you as a template you never get around to filling in.',
      },
      {
        title: 'SMS where it fits',
        body: 'Text works for the time-sensitive things: pickup ready, order out for delivery, a drop that will sell out. Used sparingly, because used otherwise it drives people away.',
      },
      {
        title: 'One customer list',
        body: 'In-store and online customers in the same list, which is what makes segmenting worth doing at all. Someone who buys weekly at the counter should not get the same email as someone who has never bought.',
      },
      {
        title: 'Loyalty and referrals',
        body: 'Loyalty and referral programs tied into the same list, so the people who already like you have a reason to bring someone else.',
      },
      {
        title: 'Reporting on what it earned',
        body: 'Revenue attributed to email, not open rates in isolation. The question worth asking is how many orders it produced.',
      },
    ],
    sections: [
      {
        heading: 'What this looks like in practice',
        body: "For Lulu's Sweets Boutique, 1,585 orders have been attributed to email marketing. That is a channel doing real work, on a list they own outright.",
      },
      {
        heading: 'Owned beats rented',
        body: 'An algorithm change can halve your reach on a social platform overnight and there is nothing you can do about it. Nobody can change the terms on your email list. It is the least fashionable channel and reliably one of the most profitable.',
      },
      {
        heading: 'Automations first, campaigns after',
        body: 'The automated messages earn the most for the least ongoing effort, so they get built first and then quietly keep working. Campaigns come after, once there is a list worth sending to and something worth saying to it.',
      },
    ],
    faqs: [
      {
        question: 'How often should I be emailing?',
        answer:
          'Less often than you fear and more often than you currently are. The automations run constantly in the background; campaigns go out when you actually have something to say: a new product, a season, an event. Sending to fill a schedule is how lists go dead.',
      },
      {
        question: 'Do I own my email list?',
        answer:
          'Yes. Your customer data is yours throughout, and if you ever decide to leave, you leave with it.',
      },
      {
        question: 'Will you write the emails as well?',
        answer:
          'Yes. Automations are written and built as part of setup, and campaign work is handled for you rather than handed over as a template to fill in.',
      },
    ],
    related: ['shopify-seo', 'shopify-store-maintenance'],
  },
];

export const serviceBySlug = new Map(services.map((s) => [s.slug, s]));

export function getService(slug: string): Service | undefined {
  return serviceBySlug.get(slug);
}
